import type { ReactNode } from "react";
import { Link } from "react-router";
import { Banknote, Clock, MapPin, Navigation, Package } from "lucide-react";
import { formatMAD } from "@tarawa/shared";
import { BorderBeam, cn } from "@tarawa/ui";
import { fmtTime, type OrderCard as Order } from "@/lib/driver";
import { DeliveryBadge, PaymentBadge } from "./ui";

/** Order card used for available orders and for the driver's own orders. */
export function OrderCard({ order, assigned, action, highlight }: { order: Order; assigned?: boolean; action?: ReactNode; highlight?: boolean }) {
  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold">{order.orderNumber}</p>
          <p className="flex items-center gap-1 text-xs text-muted">
            <Clock className="size-3.5" />
            {order.confirmedAt ? `Confirmée ${fmtTime(order.confirmedAt)}` : `Passée ${fmtTime(order.createdAt)}`}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <DeliveryBadge status={order.status} assigned={!!assigned} />
          {order.paymentStatus && <PaymentBadge status={order.paymentStatus} />}
        </div>
      </div>
      <div className="mt-3 space-y-1.5 text-sm">
        <p className="font-semibold">{order.customerName}</p>
        <p className="flex items-start gap-1.5 text-muted">
          <MapPin className="mt-0.5 size-4 shrink-0 text-brand-600" />
          <span>
            {order.addressLine}, {order.city}
            {order.hasGps && (
              <span className="ms-1 inline-flex items-center gap-0.5 text-xs font-semibold text-brand-700">
                <Navigation className="size-3" /> GPS
              </span>
            )}
          </span>
        </p>
        {order.deliveryFailureReason && <p className="text-xs font-medium text-terracotta-600">Motif : « {order.deliveryFailureReason} »</p>}
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-dashed border-line pt-3 text-sm">
        <span className="flex items-center gap-1.5 text-muted">
          <Package className="size-4" />
          {order.itemCount} article{order.itemCount > 1 ? "s" : ""} · total {formatMAD(order.totalCents)}
        </span>
        <span className="flex items-center gap-1.5 font-extrabold text-brand-800">
          <Banknote className="size-4" />
          À encaisser : {formatMAD(order.codAmountCents)}
        </span>
      </div>
    </>
  );

  return (
    <article className={cn("relative overflow-hidden rounded-[var(--radius-card)] bg-surface p-4 shadow-[var(--shadow-card)]")} aria-label={`Commande ${order.orderNumber}`}>
      {highlight && <BorderBeam size={80} duration={8} />}
      {assigned ? (
        <Link to={`/commandes/${order.id}`} className="block">
          {body}
        </Link>
      ) : (
        body
      )}
      {action && <div className="mt-3">{action}</div>}
    </article>
  );
}
